import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  Cog6ToothIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { adminAPI } from '../../config/api';
import LoadingScreen from '../../components/common/LoadingScreen';

const AdminSettings = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('privacy_policy');
  
  // Settings data
  const [settings, setSettings] = useState({
    privacy_policy: {
      title: { ar: '', en: '' },
      content: { ar: '', en: '' },
      lastUpdatedBy: null,
      updatedAt: null
    },
    refund_policy: {
      title: { ar: '', en: '' },
      content: { ar: '', en: '' },
      lastUpdatedBy: null,
      updatedAt: null
    }
  });

  // Form data
  const [formData, setFormData] = useState({
    title: { ar: '', en: '' },
    content: { ar: '', en: '' }
  });

  // Tabs configuration
  const tabs = [
    {
      id: 'privacy_policy',
      name: 'سياسة الخصوصية',
      icon: ShieldCheckIcon,
      color: 'blue'
    },
    {
      id: 'refund_policy', 
      name: 'سياسة الاسترجاع والاستبدال',
      icon: DocumentTextIcon,
      color: 'green'
    }
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    // Update form data when active tab changes
    const currentSetting = settings[activeTab];
    setFormData({
      title: { ...currentSetting.title },
      content: { ...currentSetting.content }
    });
  }, [activeTab, settings]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getSettings();
      const settingsData = response.data.data;
      
      // Organize settings by type
      const organizedSettings = {
        privacy_policy: {
          title: { ar: '', en: '' },
          content: { ar: '', en: '' },
          lastUpdatedBy: null,
          updatedAt: null
        },
        refund_policy: {
          title: { ar: '', en: '' },
          content: { ar: '', en: '' },
          lastUpdatedBy: null,
          updatedAt: null
        }
      };

      settingsData.forEach(setting => {
        organizedSettings[setting.type] = setting;
      });

      setSettings(organizedSettings);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('خطأ في تحميل الإعدادات');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, language, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [language]: value
      }
    }));
  };

  const handleSave = async () => {
    // Validation
    if (!formData.title.ar || !formData.title.en || !formData.content.ar || !formData.content.en) {
      toast.error('يرجى ملء جميع الحقول باللغتين العربية والإنجليزية');
      return;
    }

    try {
      setSaving(true);
      await adminAPI.updateSetting(activeTab, formData);
      
      // Update local state
      setSettings(prev => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          title: { ...formData.title },
          content: { ...formData.content },
          updatedAt: new Date()
        }
      }));

      toast.success('تم حفظ الإعدادات بنجاح');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.message || 'خطأ في حفظ الإعدادات');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    const currentSetting = settings[activeTab];
    setFormData({
      title: { ...currentSetting.title },
      content: { ...currentSetting.content }
    });
  };

  if (loading) {
    return <LoadingScreen />;
  }

  const currentTab = tabs.find(tab => tab.id === activeTab);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Cog6ToothIcon className="h-8 w-8 text-blue-600 ml-3" />
                إعدادات الموقع
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                إدارة سياسات الموقع والنصوص القانونية
              </p>
            </div>
            <button
              onClick={loadSettings}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <ArrowPathIcon className="h-4 w-4 ml-2" />
              تحديث
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 space-x-reverse px-6" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors ${
                  activeTab === tab.id
                    ? `border-${tab.color}-500 text-${tab.color}-600`
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-5 w-5 ml-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-lg shadow-sm border border-gray-200"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <currentTab.icon className={`h-6 w-6 text-${currentTab.color}-600 ml-3`} />
              <h2 className="text-xl font-semibold text-gray-900">{currentTab.name}</h2>
            </div>
            {settings[activeTab].updatedAt && (
              <div className="text-sm text-gray-500">
                آخر تحديث: {new Date(settings[activeTab].updatedAt).toLocaleDateString('ar-SA')}
                {settings[activeTab].lastUpdatedBy && (
                  <span className="mr-2">
                    بواسطة: {settings[activeTab].lastUpdatedBy.name}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="space-y-6">
            {/* Arabic Fields */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <span className="w-4 h-4 bg-green-500 rounded-full ml-2"></span>
                  النسخة العربية
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    العنوان
                  </label>
                  <input
                    type="text"
                    value={formData.title.ar}
                    onChange={(e) => handleInputChange('title', 'ar', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="أدخل العنوان باللغة العربية"
                    dir="rtl"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    المحتوى
                  </label>
                  <textarea
                    value={formData.content.ar}
                    onChange={(e) => handleInputChange('content', 'ar', e.target.value)}
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                    placeholder="أدخل المحتوى باللغة العربية"
                    dir="rtl"
                  />
                </div>
              </div>

              {/* English Fields */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <span className="w-4 h-4 bg-blue-500 rounded-full ml-2"></span>
                  النسخة الإنجليزية
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title.en}
                    onChange={(e) => handleInputChange('title', 'en', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter title in English"
                    dir="ltr"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content
                  </label>
                  <textarea
                    value={formData.content.en}
                    onChange={(e) => handleInputChange('content', 'en', e.target.value)}
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                    placeholder="Enter content in English"
                    dir="ltr"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-4 space-x-reverse pt-6 border-t border-gray-200">
              <button
                onClick={resetForm}
                className="flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <XMarkIcon className="h-4 w-4 ml-2" />
                إلغاء التغييرات
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors ${
                  saving ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {saving ? (
                  <ArrowPathIcon className="h-4 w-4 ml-2 animate-spin" />
                ) : (
                  <CheckIcon className="h-4 w-4 ml-2" />
                )}
                {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminSettings;